import React, { useState } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { formatCurrency, formatDate } from '../utils/helpers';
import Modal from './Modal';

function RecurringManager() {
  const { state, addRecurring, updateRecurring, deleteRecurring, toggleRecurring } = useFinance();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  
  const [form, setForm] = useState({
    type: 'expense',
    amount: '',
    description: '',
    categoryId: '',
    frequency: 'monthly',
    nextOccurrence: new Date().toISOString().split('T')[0],
    modeId: state.modes[0]?.id || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      amount: parseFloat(form.amount),
      nextOccurrence: new Date(form.nextOccurrence).toISOString(),
    };
    
    if (editingRecurring) {
      updateRecurring({ ...editingRecurring, ...data });
    } else {
      addRecurring(data);
    }
    
    setIsModalOpen(false);
  };

  const handleEdit = (recurring) => {
    setEditingRecurring(recurring);
    setForm({
      type: recurring.type,
      amount: recurring.amount.toString(),
      description: recurring.description,
      categoryId: recurring.categoryId,
      frequency: recurring.frequency,
      nextOccurrence: new Date(recurring.nextOccurrence).toISOString().split('T')[0],
      modeId: recurring.modeId || state.modes[0]?.id || '',
    });
    setIsModalOpen(true);
  };

  const openNewModal = () => {
    setEditingRecurring(null); 
    setForm({ 
        type: 'expense', 
        amount: '', 
        description: '', 
        categoryId: '', 
        frequency: 'monthly', 
        nextOccurrence: new Date().toISOString().split('T')[0],
        modeId: state.modes[0]?.id || ''
    }); 
    setIsModalOpen(true);
  };

  const getCategory = (id) => state.categories.find(c => c.id === id) || { icon: '📦', name: 'Other', color: '#64748b' };
  const getMode = (id) => state.modes.find(m => m.id === id) || { icon: '💳', name: 'Unknown' };

  const frequencyLabels = {
    daily: '🔄 Daily',
    weekly: '📅 Weekly',
    monthly: '🗓️ Monthly',
    yearly: '📆 Yearly',
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Recurring Transactions</h2>
          <p className="text-slate-400 mt-1">Automate repeated income & expenses</p>
        </div>
        <button
          onClick={openNewModal}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium hover:from-primary-500 hover:to-primary-400 transition-all"
        >
          <span>🔄</span> Add Recurring
        </button>
      </div>

      <div className="space-y-4">
          {state.recurringTransactions.map(recurring => {
            const category = getCategory(recurring.categoryId);
            const mode = getMode(recurring.modeId);
            return (
              <div key={recurring.id} className={`bg-slate-800/50 border rounded-2xl p-4 ${recurring.isActive ? 'border-slate-700' : 'border-slate-700/50 opacity-60'}`}>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
                    style={{ backgroundColor: `${category.color}20` }}
                  >
                    {category.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium truncate">{recurring.description}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${recurring.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'}`}>
                        {recurring.isActive ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                      <span>{frequencyLabels[recurring.frequency]}</span>
                      <span>•</span>
                      <span>{mode.icon} {mode.name}</span>
                      <span>•</span>
                      <span>Next: {formatDate(recurring.nextOccurrence)}</span>
                    </div>
                  </div>
                  
                  <div className={`text-right ${recurring.type === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>
                    <p className="font-semibold text-lg">
                      {recurring.type === 'income' ? '+' : '-'}
                      {formatCurrency(recurring.amount, state.currency)}
                    </p>
                    <p className="text-xs text-slate-500">{recurring.type}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRecurring(recurring.id)}
                      className={`p-2 rounded-lg transition-colors ${recurring.isActive ? 'hover:bg-amber-500/20 text-amber-400' : 'hover:bg-green-500/20 text-green-400'}`}
                      title={recurring.isActive ? 'Pause' : 'Resume'}
                    >
                      {recurring.isActive ? '⏸️' : '▶️'}
                    </button>
                    <button onClick={() => handleEdit(recurring)} className="p-2 rounded-lg hover:bg-slate-600 text-slate-400 hover:text-white transition-colors">✏️</button>
                    <button onClick={() => { if (window.confirm('Delete?')) deleteRecurring(recurring.id); }} className="p-2 rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-colors">🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingRecurring ? 'Edit Recurring Transaction' : 'Add Recurring Transaction'}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex rounded-xl bg-slate-700/50 p-1">
            <button type="button" onClick={() => setForm({...form, type: 'expense'})} className={`flex-1 py-2 rounded-lg font-medium transition-all ${form.type === 'expense' ? 'bg-rose-500 text-white' : 'text-slate-400'}`}>Expense</button>
            <button type="button" onClick={() => setForm({...form, type: 'income'})} className={`flex-1 py-2 rounded-lg font-medium transition-all ${form.type === 'income' ? 'bg-emerald-500 text-white' : 'text-slate-400'}`}>Income</button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white" />
          </div>

          <div>
             <label className="block text-sm font-medium text-slate-300 mb-2">Payment Mode</label>
             <select value={form.modeId} onChange={e => setForm({...form, modeId: e.target.value})} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white">
                 {state.modes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
             </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
            <select value={form.categoryId} onChange={e => setForm({...form, categoryId: e.target.value})} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white">
              <option value="">Select Category</option>
              {state.categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Frequency</label>
            <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})} className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white">
                {Object.entries(frequencyLabels).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Next Occurrence</label>
            <input type="date" value={form.nextOccurrence} onChange={e => setForm({...form, nextOccurrence: e.target.value})} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white" />
          </div>

          <button type="submit" className="w-full py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-500">Save</button>
        </form>
      </Modal>
    </div>
  );
}

export default RecurringManager;
